import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  onboarding,
  partnerForms,
  type PartnerLink,
} from "@/features/onboarding/api/onboarding.api";
import { cn } from "@/lib/utils";
import { toastApiError } from "@/lib/toast";

function RegenerateShell({
  onRegenerate,
  confirmTitle,
  confirmDescription,
  confirmLabel = "Create new link",
  errorMessage,
  label = "Regenerate Link",
  busyLabel = "Creating…",
  disabled,
  className,
}: {
  onRegenerate: () => Promise<void>;
  confirmTitle: string;
  confirmDescription: string;
  confirmLabel?: string;
  errorMessage: string;
  label?: string;
  busyLabel?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  async function run() {
    if (disabled) return;
    if (!(await confirm({
      title: confirmTitle,
      description: confirmDescription,
      confirmLabel,
    }))) {
      return;
    }
    setBusy(true);
    try {
      await onRegenerate();
    } catch (e) {
      toastApiError(e, errorMessage);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={run}
      disabled={busy || disabled}
      className={cn("whitespace-nowrap", className)}
    >
      <RefreshCw className={cn(busy && "animate-spin")} />
      {busy ? busyLabel : label}
    </Button>
  );
}

export default function RegenerateLinkButton({
  agentNumber,
  onDone,
  label,
  className,
}: {
  agentNumber: number;
  onDone: (code: string, path: string) => void;
  label?: string;
  className?: string;
}) {
  return (
    <RegenerateShell
      disabled={!agentNumber}
      confirmTitle="Create new link ID?"
      confirmDescription="Create a new share link ID for this agent? The current Onboarding and Verify links will stop working."
      errorMessage="Couldn't create a new link ID."
      label={label}
      className={className}
      onRegenerate={async () => {
        const r = await onboarding.regenerateCode(agentNumber);
        onDone(r.code, r.path);
      }}
    />
  );
}

export function PartnerRegenerateLinkButton({
  userId,
  userName,
  onDone,
  label = "Regenerate Link",
  className,
}: {
  userId: string;
  userName?: string | null;
  onDone: (link: PartnerLink) => void;
  label?: string;
  className?: string;
}) {
  const who = userName?.trim() ? ` for ${userName.trim()}` : "";
  return (
    <RegenerateShell
      disabled={!userId}
      confirmTitle="Create new partner link?"
      confirmDescription={`Create a new partner form link${who}? The current link will stop working immediately, including any copy already sent by email or QR code.`}
      errorMessage="Couldn't create a new partner link."
      label={label}
      className={className}
      onRegenerate={async () => {
        onDone(await partnerForms.regenerateLink(userId));
      }}
    />
  );
}
